import { ReactNode } from 'react';
import { cn } from '../shared/ui/utils';

export type AppPageTemplate = 'workspace' | 'collection' | 'form' | 'content';
type AppPageInset = 'default' | 'flush';

interface AppPageProps {
  children: ReactNode;
  template?: AppPageTemplate;
  inset?: AppPageInset;
  fillHeight?: boolean;
  className?: string;
  contentClassName?: string;
}

interface AppPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

interface AppPageSectionProps {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
  surface?: 'card' | 'plain';
}

const templateClassMap: Record<AppPageTemplate, string> = {
  workspace: 'flex h-full min-h-0 flex-col gap-4 md:gap-6',
  collection: 'flex min-h-full flex-col gap-4 md:gap-6',
  form: 'mx-auto flex min-h-full w-full max-w-5xl flex-col gap-5 md:gap-6',
  content: 'mx-auto flex min-h-full w-full max-w-6xl flex-col gap-5 md:gap-6',
};

const insetClassMap: Record<AppPageInset, string> = {
  default: 'px-4 py-4 md:px-6 md:py-6 lg:px-8 lg:py-8',
  flush: '',
};

export function AppPage({
  children,
  template = 'content',
  inset = 'default',
  fillHeight = false,
  className,
  contentClassName,
}: AppPageProps) {
  return (
    <section
      data-slot="app-page"
      data-template={template}
      className={cn('w-full min-w-0 min-h-0 font-apple', fillHeight && 'h-full', className)}
    >
      <div
        data-slot="app-page-content"
        className={cn(
          'w-full min-w-0 min-h-0',
          fillHeight && 'h-full',
          templateClassMap[template],
          insetClassMap[inset],
          contentClassName
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function AppPageHeader({
  title,
  description,
  eyebrow,
  actions,
  className,
}: AppPageHeaderProps) {
  return (
    <header
      data-slot="app-page-header"
      className={cn(
        'flex min-w-0 flex-col gap-3 pb-4 lg:flex-row lg:items-start lg:justify-between',
        className
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        {eyebrow ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="truncate text-[length:var(--text-h2)] font-bold tracking-tight text-foreground">
          {title}
        </h1>
        {description ? (
          <p className="max-w-3xl text-[length:var(--text-menu)] text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 lg:shrink-0 [&>*]:max-w-full [&>*]:flex-wrap">{actions}</div> : null}
    </header>
  );
}

export function AppPageSection({
  children,
  title,
  description,
  actions,
  className,
  contentClassName,
  surface = 'card',
}: AppPageSectionProps) {
  return (
    <section
      data-slot="app-page-section"
      data-surface={surface}
      className={cn(
        'min-w-0',
        surface === 'card' &&
          'rounded-none border border-border bg-card glass-shadow',
        className
      )}
    >
      <div className={cn(surface === 'card' ? 'p-4 md:p-6' : '', contentClassName)}>
        {title || description || actions ? (
          <header className="mb-4 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1 space-y-1">
              {title ? (
                <h2 className="text-[length:var(--text-h4)] font-semibold text-foreground">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p className="text-[length:var(--text-menu)] text-muted-foreground">
                  {description}
                </p>
              ) : null}
            </div>
            {actions ? <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 lg:shrink-0 [&>*]:max-w-full [&>*]:flex-wrap">{actions}</div> : null}
          </header>
        ) : null}
        {children}
      </div>
    </section>
  );
}
